import { useEffect, useState, useCallback, useMemo } from "react";
import { Contact, Plus, Trash2, Pencil, Upload, Download, FileSpreadsheet, ShieldCheck, Sparkles, ChevronDown, ChevronUp, ArrowUpDown, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import TemplateAnalysisDialog from "@/components/TemplateAnalysisDialog";
import CustomerImportanceBadge from "@/components/CustomerImportanceBadge";
import { CardCaptureDialog } from "@/components/CardCaptureDialog";

const EMPTY = {
  name: "", seller: "", email: "", contact_person: "", phone: "", title: "", website: "",
  address: "", destination_port: "", payment_terms: "", currency: "USD",
  price_multiplier: 1, shipping_fee: 0,
  importance_level: 0, customer_type: "", importance_reason: "", assessment_report: "",
};

export default function Customers() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isExport = user?.role === "export";
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, {id?} = dialog open
  const [analysis, setAnalysis] = useState(null);
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachPickId, setAttachPickId] = useState(""); // { templateName, data } — non-null shows the dialog
  // 2026-10-27 · 名片扫描 / 筛选 / 排序状态
  const [cardOpen, setCardOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [filterImportance, setFilterImportance] = useState("all"); // "all" | "1".."5"
  const [sortByImp, setSortByImp] = useState(false);

  // Derived list — applies importance filter / sort before rendering.
  const visibleCustomers = useMemo(() => {
    let list = customers;
    if (filterImportance !== "all") {
      const want = Number(filterImportance);
      list = list.filter((c) => Number(c.importance_level || 0) === want);
    }
    if (sortByImp) {
      // Highest first; untyped (0) goes to the bottom.
      list = [...list].sort((a, b) =>
        (Number(b.importance_level || 0) - Number(a.importance_level || 0))
        || String(a.name || "").localeCompare(String(b.name || ""))
      );
    }
    return list;
  }, [customers, filterImportance, sortByImp]);

  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSave = async () => {
    try {
      if (editing.id) {
        await api.put(`/customers/${editing.id}`, editing);
      } else {
        await api.post("/customers", editing);
      }
      setEditing(null);
      fetchCustomers();
      toast.success(t("common.save"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      fetchCustomers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleAddTemplate = async (file) => {
    if (!file || !editing?.id) return;
    if (!/\.(xlsx|xlsm)$/i.test(file.name)) {
      toast.error(t("customers.templateXlsxOnly"));
      return;
    }
    const suggested = file.name.replace(/\.(xlsx|xlsm)$/i, "");
    const name = window.prompt(t("customers.templateNamePrompt"), suggested);
    if (!name || !name.trim()) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/customers/${editing.id}/quote-templates?name=${encodeURIComponent(name.trim())}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((cur) => ({ ...cur, quote_templates: data.customer.quote_templates }));
      fetchCustomers();
      toast.success(t("customers.templateSaved"));
      // Immediately surface the compatibility check so the user can spot typos before the first export.
      if (data.analysis) {
        setAnalysis({ templateName: name.trim(), data: data.analysis });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleCheckTemplate = async (templateId, templateName) => {
    if (!editing?.id) return;
    try {
      const { data } = await api.get(`/customers/${editing.id}/quote-templates/${templateId}/analyze`);
      setAnalysis({ templateId, templateName, data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleAutofixTemplate = async () => {
    if (!editing?.id || !analysis?.templateId) return;
    try {
      const { data } = await api.post(`/customers/${editing.id}/quote-templates/${analysis.templateId}/autofix`);
      toast.success(t("templateAnalysis.autofixSuccess").replace("{count}", data.fixed_count));
      setAnalysis({ ...analysis, data: data.analysis });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("templateAnalysis.autofixFailed"));
    }
  };

  // Open the "attach from system catalog" picker — fetches the shared library
  // and pre-selects the first template that isn't already attached to this customer.
  const openAttachTemplate = async () => {
    try {
      const { data } = await api.get("/system-quote-templates");
      setSystemTemplates(data || []);
      const attachedIds = new Set((editing?.quote_templates || []).map((t) => t.system_template_id).filter(Boolean));
      const first = (data || []).find((s) => !attachedIds.has(s.id));
      setAttachPickId(first?.id || (data?.[0]?.id ?? ""));
      setAttachOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleAttachTemplate = async () => {
    if (!attachPickId || !editing?.id) return;
    try {
      const { data } = await api.post(`/customers/${editing.id}/quote-templates/attach`,
        { system_template_id: attachPickId });
      setEditing((cur) => ({ ...cur, quote_templates: data.customer.quote_templates }));
      fetchCustomers();
      toast.success(t("customers.templateAttached"));
      setAttachOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleRemoveTemplate = async (templateId, name) => {
    if (!editing?.id) return;
    if (!window.confirm(`${t("common.delete")} "${name}"?`)) return;
    try {
      await api.delete(`/customers/${editing.id}/quote-templates/${templateId}`);
      setEditing((cur) => ({
        ...cur,
        quote_templates: (cur.quote_templates || []).filter((x) => x.id !== templateId),
        quote_template_url: templateId === "legacy" ? "" : cur.quote_template_url,
      }));
      fetchCustomers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setField = (field, value) => setEditing((c) => ({ ...c, [field]: value }));

  // 2026-10-27 · 顶部"扫描名片新增"快捷入口 —— 把 OCR 结果直接推进 editing 对话框
  //   若已有编辑中的对话框，就把名片数据合并进去 (AI 评级覆盖式写入)；
  //   否则新建一个客户。
  const applyCardToNewCustomer = (card) => {
    setEditing((cur) => {
      const base = cur ? { ...cur } : { ...EMPTY, seller: isExport ? user?.name || "" : "" };
      return {
        ...base,
        name: card.name || base.name,
        contact_person: card.contact_person || base.contact_person,
        email: card.email || base.email,
        phone: card.phone || base.phone,
        title: card.title || base.title,
        website: card.website || base.website,
        address: card.address || base.address,
        // AI fields are always refreshed with the latest scan
        importance_level: card.importance_level ?? base.importance_level ?? 0,
        customer_type: card.customer_type || base.customer_type || "",
        importance_reason: card.importance_reason || base.importance_reason || "",
        assessment_report: card.assessment_report || base.assessment_report || "",
      };
    });
    if (card.assessment_report) setReportOpen(true);
  };

  return (
    <div className="animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h1 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
          <Contact size={24} /> {t("customers.title")}
        </h1>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* 2026-10-27 · 按重要性筛选 */}
          <Select value={filterImportance} onValueChange={setFilterImportance}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="customers-importance-filter">
              <SelectValue placeholder={t("customers.filterByImportance") || "按重要性筛选"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("customers.allImportance") || "全部评级"}</SelectItem>
              <SelectItem value="5">⭐⭐⭐⭐⭐ {t("importance.5")}</SelectItem>
              <SelectItem value="4">⭐⭐⭐⭐ {t("importance.4")}</SelectItem>
              <SelectItem value="3">⭐⭐⭐ {t("importance.3")}</SelectItem>
              <SelectItem value="2">⭐⭐ {t("importance.2")}</SelectItem>
              <SelectItem value="1">⭐ {t("importance.1")}</SelectItem>
              <SelectItem value="0">— {t("customers.notRated") || "未评级"}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant={sortByImp ? "default" : "outline"}
            size="sm"
            onClick={() => setSortByImp((v) => !v)}
            className="h-9 rounded-md"
            data-testid="customers-sort-by-importance"
            title={t("customers.sortByImportance") || "按重要性排序"}
          >
            <ArrowUpDown size={13} />
            <span className="hidden sm:inline ml-1 text-xs">{t("customers.sortByImportance") || "按重要性"}</span>
          </Button>
          {/* 2026-10-27 · 顶部扫描名片 —— 一拍即建客户 (手机端最常用) */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCardOpen(true)}
            className="h-9 rounded-md border-amber-400 text-amber-700 hover:bg-amber-50"
            data-testid="customers-scan-card-button"
          >
            <Sparkles size={13} className="mr-1" />
            <span className="hidden xs:inline">{t("customers.scanCard") || "拍名片自动填"}</span>
            <span className="xs:hidden">{t("customers.scanCard") || "拍名片"}</span>
          </Button>
          <Button onClick={() => setEditing({ ...EMPTY, seller: isExport ? user.name : "" })} data-testid="new-customer-button" className="h-9 rounded-md bg-brand hover:bg-brand-hover transition-colors">
            <Plus size={16} /> {t("quote.new")}
          </Button>
        </div>
      </div>

      {/* Desktop / tablet — table view */}
      <div className="hidden md:block bg-card border border-border rounded-md overflow-hidden">
        <Table data-testid="customers-table">
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase w-[140px]">{t("customers.importance") || "重要性"}</TableHead>
              <TableHead className="text-xs uppercase">{t("customers.name")}</TableHead>
              <TableHead className="text-xs uppercase">{t("customers.seller")}</TableHead>
              <TableHead className="text-xs uppercase">{t("customers.contactPerson")}</TableHead>
              <TableHead className="text-xs uppercase">{t("customers.phone") || "电话"}</TableHead>
              <TableHead className="text-xs uppercase">{t("customers.email")}</TableHead>
              <TableHead className="text-xs uppercase">{t("customers.address")}</TableHead>
              <TableHead className="text-xs uppercase">{t("customers.destinationPort")}</TableHead>
              <TableHead className="text-xs uppercase">{t("customers.currency")}</TableHead>
              <TableHead className="text-xs uppercase text-right">{t("customers.priceMultiplier")}</TableHead>
              <TableHead className="text-xs uppercase text-right w-24">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleCustomers.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-10 text-muted-foreground" data-testid="customers-empty-state">
                  {t("customers.noCustomers")}
                </TableCell>
              </TableRow>
            )}
            {visibleCustomers.map((c) => {
              const lv = Number(c.importance_level || 0);
              const highlight = lv >= 4;  // 核心/重点客户整行加亮
              return (
              <TableRow
                key={c.id}
                data-testid={`customer-row-${c.name}`}
                className={`hover:bg-secondary/40 ${highlight ? "bg-amber-50/60 hover:bg-amber-100/60" : ""}`}
              >
                <TableCell className="py-2">
                  <CustomerImportanceBadge level={lv} customerType={c.customer_type} compact />
                </TableCell>
                <TableCell className={`font-semibold text-sm ${highlight ? "text-amber-900" : ""}`}>
                  <div className="flex items-center gap-1">
                    {highlight && <Star size={11} fill="currentColor" className="text-amber-500 shrink-0" />}
                    <span>{c.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm" data-testid={`customer-seller-${c.name}`}>{c.seller || "-"}</TableCell>
                <TableCell className="text-sm">{c.contact_person || "-"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.phone || "-"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.email || "-"}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate" title={c.address}>{c.address || "-"}</TableCell>
                <TableCell className="text-sm">{c.destination_port || "-"}</TableCell>
                <TableCell className="text-sm font-mono" data-testid={`customer-currency-${c.name}`}>{c.currency || "USD"}</TableCell>
                <TableCell className="text-sm font-mono text-right" data-testid={`customer-multiplier-${c.name}`}>
                  {c.price_multiplier && c.price_multiplier !== 1 ? `× ${c.price_multiplier}` : "-"}
                </TableCell>
                <TableCell className="text-right p-1">
                  <div className="flex justify-end">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing({ ...c })} title={t("common.edit")} data-testid={`edit-customer-${c.name}`}>
                      <Pencil size={13} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(c.id)} title={t("common.delete")} data-testid={`delete-customer-${c.name}`}>
                      <Trash2 size={13} className="text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </div>

      {/* 2026-10-27 · Mobile card view — 一屏突出重要性，支持点击编辑 */}
      <div className="md:hidden space-y-2" data-testid="customers-mobile-list">
        {visibleCustomers.length === 0 && (
          <div className="text-center py-10 text-muted-foreground text-sm bg-card border border-border rounded-md" data-testid="customers-mobile-empty">
            {t("customers.noCustomers")}
          </div>
        )}
        {visibleCustomers.map((c) => {
          const lv = Number(c.importance_level || 0);
          const highlight = lv >= 4;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setEditing({ ...c })}
              className={`w-full text-left bg-card border rounded-lg p-3 active:scale-[0.99] transition
                ${highlight ? "border-amber-300 ring-1 ring-amber-200 bg-amber-50/40" : "border-border"}`}
              data-testid={`customer-card-${c.name}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold truncate ${highlight ? "text-amber-900" : ""}`}>
                    {highlight && <Star size={12} fill="currentColor" className="inline mr-1 text-amber-500" />}
                    {c.name}
                  </div>
                  {c.contact_person && (
                    <div className="text-[11px] text-muted-foreground truncate">{c.contact_person}{c.title ? ` · ${c.title}` : ""}</div>
                  )}
                </div>
                <CustomerImportanceBadge level={lv} customerType={c.customer_type} compact />
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                {c.phone && <span>📞 {c.phone}</span>}
                {c.email && <span className="truncate max-w-[60%]">✉️ {c.email}</span>}
                {c.destination_port && <span>🚢 {c.destination_port}</span>}
              </div>
              {c.importance_reason && (
                <div className="mt-1.5 text-[11px] text-amber-800/80 bg-amber-50 rounded px-1.5 py-1 border border-amber-100 line-clamp-2">
                  💡 {c.importance_reason}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => { if (!o) { setEditing(null); setReportOpen(false); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center justify-between gap-2">
              <span>{editing?.id ? t("customers.editTitle") : t("customers.newTitle")}</span>
              {!editing?.id && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setCardOpen(true)}
                  className="h-8 rounded-md border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="customer-dialog-scan-card-button"
                >
                  <Sparkles size={13} className="mr-1" />
                  {t("customers.scanCard") || "拍名片自动填"}
                </Button>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">{t("customers.title")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* 2026-10-27 · AI 客户画像面板 — 名片扫描后自动填充，不可手动修改 */}
            {(editing?.importance_level > 0 || editing?.importance_reason || editing?.assessment_report) && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 space-y-2"
                data-testid="customer-ai-importance-panel"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={14} className="text-amber-500" />
                    <span className="text-xs font-semibold text-amber-900">
                      {t("customers.importance") || "客户重要性"}
                    </span>
                  </div>
                  <CustomerImportanceBadge
                    level={editing?.importance_level}
                    customerType={editing?.customer_type}
                  />
                </div>
                {editing?.importance_reason && (
                  <p
                    className="text-[11px] leading-snug text-amber-900/90"
                    data-testid="customer-ai-importance-reason"
                  >
                    {editing.importance_reason}
                  </p>
                )}
                {editing?.assessment_report && (
                  <div className="pt-1 border-t border-amber-200/70">
                    <button
                      type="button"
                      onClick={() => setReportOpen((v) => !v)}
                      className="w-full flex items-center justify-between gap-1 text-[11px] font-medium text-amber-900 hover:text-amber-700"
                      data-testid="customer-ai-report-toggle"
                    >
                      <span>
                        {reportOpen
                          ? (t("customers.hideReport") || "收起")
                          : (t("customers.showReport") || "查看完整评估报告")}
                      </span>
                      {reportOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                    {reportOpen && (
                      <div
                        className="mt-1.5 text-[11px] leading-relaxed text-slate-800 whitespace-pre-wrap break-words"
                        data-testid="customer-ai-assessment-report"
                        dangerouslySetInnerHTML={{
                          __html: (editing.assessment_report || "")
                            .replace(/&/g, "&amp;")
                            .replace(/</g, "&lt;")
                            .replace(/>/g, "&gt;")
                            .replace(/\*\*(.+?)\*\*/g, '<strong class="text-amber-900">$1</strong>'),
                        }}
                      />
                    )}
                  </div>
                )}
                <p className="text-[10px] text-amber-700/70 italic">
                  {t("customers.importanceAiOnly") || "由 AI 根据名片智能评定，不可手动修改"}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("customers.name")} *</Label>
                <Input value={editing?.name || ""} onChange={(e) => setField("name", e.target.value)} data-testid="customer-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("customers.seller")}</Label>
                <Input value={editing?.seller || ""} disabled={isExport} onChange={(e) => setField("seller", e.target.value)} data-testid="customer-seller-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("customers.email")}</Label>
                <Input type="email" value={editing?.email || ""} onChange={(e) => setField("email", e.target.value)} data-testid="customer-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("customers.contactPerson")}</Label>
                <Input value={editing?.contact_person || ""} onChange={(e) => setField("contact_person", e.target.value)} data-testid="customer-contact-input" />
              </div>
            </div>
            {/* 2026-10-27 · 联系电话 + 职位 + 网址 */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("customers.phone") || "联系电话"}</Label>
                <Input value={editing?.phone || ""} onChange={(e) => setField("phone", e.target.value)}
                  data-testid="customer-phone-input"
                  placeholder="+1 212-555-0100" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("customers.title_label") || "职位"}</Label>
                <Input value={editing?.title || ""} onChange={(e) => setField("title", e.target.value)}
                  data-testid="customer-title-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.website") || "公司网址"}</Label>
              <Input value={editing?.website || ""} onChange={(e) => setField("website", e.target.value)}
                data-testid="customer-website-input"
                placeholder="example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.address")}</Label>
              <Textarea rows={2} value={editing?.address || ""} onChange={(e) => setField("address", e.target.value)} data-testid="customer-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("customers.destinationPort")}</Label>
                <Input value={editing?.destination_port || ""} onChange={(e) => setField("destination_port", e.target.value)} data-testid="customer-port-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("customers.shippingFee")}</Label>
                <Input
                  type="number" step="0.01" min="0" placeholder="0"
                  value={editing?.shipping_fee ?? 0}
                  onChange={(e) => setField("shipping_fee", parseFloat(e.target.value) || 0)}
                  data-testid="customer-shipping-fee-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.paymentTerms")}</Label>
              <Textarea rows={2} value={editing?.payment_terms || ""} onChange={(e) => setField("payment_terms", e.target.value)} data-testid="customer-payment-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>{t("customers.currency")}</Label>
                <Select value={editing?.currency || "USD"} onValueChange={(v) => setField("currency", v)}>
                  <SelectTrigger data-testid="customer-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD 美金</SelectItem>
                    <SelectItem value="CAD">CAD 加币</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>{t("customers.priceMultiplier")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editing?.price_multiplier ?? 1}
                  onChange={(e) => setField("price_multiplier", parseFloat(e.target.value) || 0)}
                  data-testid="customer-price-multiplier-input"
                  placeholder="1.0"
                />
                <p className="text-[11px] text-muted-foreground">{t("customers.priceMultiplierHint")}</p>
              </div>
            </div>
            {editing?.id && (
              <div className="space-y-2 pt-2 border-t border-border">
                <div className="space-y-1.5">
                  {(editing.quote_templates || []).map((tk) => (
                    <div key={tk.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border border-border bg-secondary/30" data-testid={`customer-template-row-${tk.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileSpreadsheet size={13} className="text-brand shrink-0" />
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}${tk.url}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs font-medium text-brand hover:underline truncate"
                        >
                          {tk.name}
                        </a>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="h-6 text-xs px-2"
                          title={t("customers.templateCheck")}
                          data-testid={`customer-template-check-${tk.id}`}
                          onClick={() => handleCheckTemplate(tk.id, tk.name)}>
                          <ShieldCheck size={12} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 text-destructive text-xs"
                          data-testid={`customer-template-delete-${tk.id}`}
                          onClick={() => handleRemoveTemplate(tk.id, tk.name)}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <button type="button"
                    onClick={openAttachTemplate}
                    data-testid="customer-template-attach-button"
                    className="inline-flex items-center gap-1.5 text-xs cursor-pointer px-3 py-1.5 rounded-md border border-dashed border-border hover:border-brand hover:text-brand transition-colors"
                  >
                    <Plus size={13} /> {t("customers.templateAdd")}
                  </button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-md">{t("common.cancel")}</Button>
            <Button onClick={handleSave} data-testid="save-customer-button" className="rounded-md bg-brand hover:bg-brand-hover">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TemplateAnalysisDialog
        open={!!analysis}
        onClose={() => setAnalysis(null)}
        templateName={analysis?.templateName}
        analysis={analysis?.data}
        onAutofix={analysis?.templateId ? handleAutofixTemplate : undefined}
      />

      <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
        <DialogContent className="max-w-md" data-testid="attach-system-template-dialog">
          <DialogHeader>
            <DialogTitle>{t("customers.attachTemplateTitle")}</DialogTitle>
            <DialogDescription>{t("customers.attachTemplateHint")}</DialogDescription>
          </DialogHeader>
          {systemTemplates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="no-system-templates">
              {t("customers.noSystemTemplates")}
            </p>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs">{t("customers.pickTemplate")}</Label>
              <select
                value={attachPickId}
                onChange={(e) => setAttachPickId(e.target.value)}
                data-testid="attach-template-pick-select"
                className="w-full h-10 px-3 rounded-md border border-input bg-transparent text-sm focus:border-brand focus:outline-none"
              >
                {systemTemplates.map((s) => {
                  const already = (editing?.quote_templates || []).some((x) => x.system_template_id === s.id);
                  return (
                    <option key={s.id} value={s.id} disabled={already}>
                      {s.name}{already ? "  ·  已添加" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachOpen(false)} className="rounded-md">{t("common.cancel")}</Button>
            <Button onClick={handleAttachTemplate} disabled={!attachPickId}
              className="rounded-md bg-brand hover:bg-brand-hover"
              data-testid="attach-template-confirm-button">
              {t("customers.templateAdd")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-10-27 · 顶部"拍名片"快捷入口 —— 识别后合并进 editing 对话框 */}
      <CardCaptureDialog
        open={cardOpen}
        onOpenChange={setCardOpen}
        onDetect={applyCardToNewCustomer}
      />
    </div>
  );
}
